import React, { useState, useEffect } from 'react';
import { Pencil, Trash2, Plus, ArrowLeft, ChevronDown } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { APP_TOKEN } from '../../../../constants';
import './roles-mobile.css';

const RolesList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [expandedRoleId, setExpandedRoleId] = useState(null);
  const updatedRoleId = location.state?.updatedRoleId;

  const moveRoleToTop = (list, targetId) => {
    if (!targetId || !Array.isArray(list) || list.length === 0) return list;
    const index = list.findIndex((role) => role.id === targetId);
    if (index <= 0) return list;
    const targetRole = list[index];
    const rest = list.filter((_, i) => i !== index);
    return [targetRole, ...rest];
  };

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError(null);

      const userData = JSON.parse(localStorage.getItem(APP_TOKEN));

      if (!userData || !userData.token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;

      // eslint-disable-next-line no-console
      console.log('🔧 Fetching roles from:', graphqlEndpoint);
      // eslint-disable-next-line no-console
      console.log(`🔑 Token: ${userData.token.substring(0, 20)}...`);

      const graphqlPayload = {
        query: 'query {roles {id name createdAt permissions{id name} } }',
      };

      const response = await axios.post(graphqlEndpoint, graphqlPayload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userData.token}`,
        },
      });

      // eslint-disable-next-line no-console
      console.log('✅ API Response:', response.data);

      if (response.status === 200 && response.data?.data?.roles) {
        const transformedRoles = response.data.data.roles.map((role) => ({
          id: role.id,
          name: role.name,
          createdAt: role.createdAt,
          permissions: role.permissions.map((p) => p.name),
        }));

        // eslint-disable-next-line no-console
        console.log('✅ Transformed Roles:', transformedRoles);
        setRoles(moveRoleToTop(transformedRoles, updatedRoleId));
      } else if (response.data?.errors) {
        console.error('❌ GraphQL Errors:', response.data.errors);
        setError(response.data.errors[0]?.message || 'Failed to fetch roles');
      } else {
        setError('Failed to fetch roles');
      }
    } catch (err) {
      console.error('❌ Error fetching roles:', err);

      if (err.response) {
        console.error('Server Error:', err.response.data);
        setError(
          err.response.data?.errors?.[0]?.message ||
            err.response.data?.message ||
            'Server error occurred',
        );
      } else if (err.request) {
        console.error('Network Error:', err.request);
        setError('Network error - Unable to reach server');
      } else {
        console.error('Error:', err.message);
        setError(err.message || 'An error occurred while fetching roles');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedRoleId]);

  useEffect(() => {
    if (!updatedRoleId) return;
    setRoles((prev) => moveRoleToTop(prev, updatedRoleId));
  }, [updatedRoleId]);

  const handleBackToStaff = () => {
    navigate('/staff');
  };

  const handleAddRole = () => {
    navigate('/staff/add-role');
  };

  const handleEditRole = (roleId, roleName) => {
    navigate('/staff/edit-role', { state: { roleId, roleName } });
  };

  const openDeleteModal = (roleId, roleName) => {
    setSelectedRole({ id: roleId, name: roleName });
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedRole(null);
  };

  const confirmDelete = async () => {
    if (!selectedRole) return;

    try {
      const userData = JSON.parse(localStorage.getItem(APP_TOKEN));
      const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;
      const deleteMutation = {
        query: `mutation DeleteRole($id: ID!) { 
          deleteRole(id: $id)
        }`,
        variables: {
          id: selectedRole.id,
        },
      };

      // eslint-disable-next-line no-console
      console.log('🗑️ Deleting role:', selectedRole);

      const response = await axios.post(graphqlEndpoint, deleteMutation, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userData.token}`,
        },
      });
      // eslint-disable-next-line no-console
      console.log('✅ Delete Response:', response.data);

      // Check if the response is successful
      if (response.data?.data?.deleteRole) {
        const message = response.data.data.deleteRole;
        // eslint-disable-next-line no-console
        console.log('✅ Role deleted successfully:', message);

        // Close modal
        closeDeleteModal();

        // Show success toast
        toast.success(message || 'Role deleted successfully', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        // Refresh the roles list
        fetchRoles();
      } else if (response.data?.errors) {
        console.error('❌ GraphQL Errors:', response.data.errors);
        const errorMessage =
          response.data.errors[0]?.message || 'Failed to delete role';

        closeDeleteModal();
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 3000,
        });
      } else {
        closeDeleteModal();
        toast.error('Failed to delete role', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (err) {
      console.error('❌ Error deleting role:', err);

      const errorMessage =
        err.response?.data?.errors?.[0]?.message ||
        err.response?.data?.message ||
        err.message ||
        'Error deleting role';

      closeDeleteModal();
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  // Loading state
  if (loading) {
    return (
      <div
        className="p-4"
        style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}
      >
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '50vh' }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        className="p-4"
        style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}
      >
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-3">
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center gap-2"
              onClick={handleBackToStaff}
            >
              <ArrowLeft size={18} />
            </button>
            <h4 className="mb-0">Edit Roles List</h4>
          </div>
        </div>
        <div className="alert alert-danger" role="alert">
          <h5 className="alert-heading">Error</h5>
          <p>{error}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={fetchRoles}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="p-4 roles-page"
        style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}
      >
        <div className="d-flex justify-content-between align-items-center mb-4 roles-header">
          <div className="d-flex align-items-center gap-3 roles-header-left">
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center gap-2 roles-back-btn"
              onClick={handleBackToStaff}
            >
              <ArrowLeft size={18} />
            </button>
            <h4 className="mb-0"> Roles List</h4>
          </div>
          <button
            type="button"
            className="btn d-flex align-items-center gap-2 roles-add-btn"
            onClick={handleAddRole}
            style={{ background: 'rgba(151, 142, 135, 0.11)', color: 'black' }}
          >
            <Plus size={18} />
            Add Role
          </button>
        </div>

        {roles.length === 0 ? (
          <div className="card border-0 shadow-sm p-5 text-center">
            <p className="text-muted mb-0">No roles found</p>
          </div>
        ) : (
          <div className="card border-0 shadow-sm roles-list-card">
            <div className="roles-mobile-list">
              {roles.map((role) => {
                const isExpanded = expandedRoleId === role.id;
                return (
                  <div className="roles-mobile-card" key={`mobile-${role.id}`}>
                    <div className="roles-mobile-card__summary">
                      <div className="roles-mobile-card__identity">
                        <div className="roles-mobile-card__name">
                          {role.name}
                        </div>
                        <div className="roles-mobile-card__meta">
                          {role.permissions.length} permissions
                        </div>
                      </div>
                      <div className="roles-mobile-card__tools">
                        <button
                          type="button"
                          className="roles-mobile-card__icon-btn"
                          onClick={() => handleEditRole(role.id, role.name)}
                          aria-label={`Edit ${role.name}`}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          className="roles-mobile-card__icon-btn danger"
                          onClick={() => openDeleteModal(role.id, role.name)}
                          aria-label={`Delete ${role.name}`}
                        >
                          <Trash2 size={14} />
                        </button>
                        <button
                          type="button"
                          className={`roles-mobile-card__expand-toggle ${
                            isExpanded ? 'is-open' : ''
                          }`}
                          onClick={() => {
                            setExpandedRoleId((prevId) => {
                              return prevId === role.id ? null : role.id;
                            });
                          }}
                          aria-expanded={isExpanded}
                          aria-label={
                            isExpanded
                              ? `Collapse ${role.name} permissions`
                              : `Expand ${role.name} permissions`
                          }
                        >
                          <ChevronDown size={14} />
                        </button>
                      </div>
                    </div>
                    {isExpanded ? (
                      <div className="roles-mobile-card__body">
                        {role.permissions.length > 0 ? (
                          <div className="roles-mobile-permission-chips">
                            {role.permissions.map((permission) => (
                              <span
                                className="roles-mobile-permission-chip"
                                key={`${role.id}-${permission}`}
                              >
                                {permission}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div className="roles-mobile-empty">
                            No permissions assigned
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <div className="table-responsive roles-desktop-table">
              <table className="table table-hover mb-0">
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th className="ps-4 py-3 border-0">Role</th>
                    <th className="py-3 border-0">Permissions</th>
                    <th className="py-3 border-0 text-end pe-4">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map((role) => (
                    <tr key={role.id}>
                      <td className="ps-4 py-3 align-top" data-label="Role">
                        <span className="fw-medium">
                          {role.name} ({role.permissions.length})
                        </span>
                      </td>
                      <td className="py-3" data-label="Permissions">
                        <div className="d-flex flex-wrap gap-2">
                          {role.permissions.length > 0 ? (
                            <>
                              {role.permissions
                                .slice(0, 3)
                                .map((permission) => (
                                  <span
                                    key={`${role.id}-${permission}`}
                                    className="badge"
                                    style={{
                                      backgroundColor:
                                        'rgba(151, 142, 135, 0.11)',
                                      color: 'black',
                                      fontWeight: 'normal',
                                      padding: '6px 12px',
                                      fontSize: '12px',
                                    }}
                                  >
                                    {permission}
                                  </span>
                                ))}
                              {role.permissions.length > 3 && (
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor:
                                      'rgba(151, 142, 135, 0.11)',
                                    color: 'black',
                                    fontWeight: 'normal',
                                    padding: '6px 12px',
                                    fontSize: '12px',
                                  }}
                                >
                                  ...
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-muted">
                              No permissions assigned
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        className="py-3 align-top text-end pe-4"
                        data-label="Action"
                      >
                        <div className="d-flex gap-2 justify-content-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            style={{ padding: '4px 8px' }}
                            onClick={() => handleEditRole(role.id, role.name)}
                            title="Edit Role"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            style={{ padding: '4px 8px' }}
                            onClick={() => openDeleteModal(role.id, role.name)}
                            title="Delete Role"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '8px' }}>
              <div className="modal-body text-center p-4">
                <h5 className="mb-3">
                  Do you really want to remove this role?
                </h5>
                <div className="d-flex gap-3 justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-4"
                    onClick={closeDeleteModal}
                    style={{
                      borderRadius: '25px',
                      minWidth: '100px',
                    }}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger px-4"
                    onClick={confirmDelete}
                    style={{
                      borderRadius: '25px',
                      minWidth: '100px',
                      backgroundColor: '#dc3545',
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RolesList;
